import {
  DocketDisplayItemTypeHeight,
  CONTINUE,
} from '../../../common/constants';
import {
  DocketDisplayItem,
  DocketDisplayItemType,
  DocketDisplayOrderItem,
} from '../../../common/types';

export const getDocketItems = (
  items: DocketDisplayItem[],
  docketId: string,
  startIndex: number,
): DocketDisplayItem[] => {
  const result = [items[startIndex]];

  for (let i = 0; i < items.length; i++) {
    if (
      i !== startIndex &&
      items[i].type !== DocketDisplayItemType.DOCKET_HEADER &&
      'docketId' in items[i] &&
      (items[i] as DocketDisplayOrderItem).docketId === docketId
    ) {
      result.push(items[i]);
    }
  }

  return result;
};

export const getHeight = (
  docketItems: DocketDisplayItem[],
  scale: (n: number) => number,
): number => docketItems.reduce((sum, item) => sum + scale(item.height), 0);

export const isDocketLarge = (
  docketItems: DocketDisplayItem[],
  rowHeight: number,
  scale: (n: number) => number,
): boolean => getHeight(docketItems, scale) > rowHeight;

export const wouldOverflowColumn = (
  docketItems: DocketDisplayItem[],
  maxHeight: number,
  scale: (n: number) => number,
): boolean => getHeight(docketItems, scale) > maxHeight;

// A notifier header must stay with its first row, so it needs room for the rows up to
// and including the first notifier item or summary row.
const getNotifierLeadHeight = (
  items: DocketDisplayItem[],
  headerIndex: number,
  scale: (n: number) => number,
): number => {
  let height = scale(items[headerIndex].height);
  for (let i = headerIndex + 1; i < items.length; i++) {
    const type = items[i].type;
    if (
      type !== DocketDisplayItemType.NOTIFIER_PROFILE &&
      type !== DocketDisplayItemType.NOTIFIER_ITEM &&
      type !== DocketDisplayItemType.NOTIFIER_SUMMARY
    ) {
      break;
    }
    height += scale(items[i].height);
    if (
      type === DocketDisplayItemType.NOTIFIER_ITEM ||
      type === DocketDisplayItemType.NOTIFIER_SUMMARY
    ) {
      break;
    }
  }
  return height;
};

export const splitDocketIntoChunks = (
  docketItems: DocketDisplayItem[],
  maxHeight: number,
  rowHeight: number,
  scale: (n: number) => number,
): DocketDisplayItem[][] => {
  const filteredItems = docketItems.filter(
    item =>
      item.type !== DocketDisplayItemType.CONTINUE &&
      item.type !== DocketDisplayItemType.CONTINUED,
  );

  if (filteredItems.length === 0) return [];

  const chunks: DocketDisplayItem[][] = [];
  let currentChunk: DocketDisplayItem[] = [filteredItems[0]];
  let currentHeight = scale(filteredItems[0].height);

  const continueHeight = scale(
    DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUE],
  );
  const continuedHeight = scale(
    DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUED],
  );

  for (let i = 1; i < filteredItems.length; i++) {
    const item = filteredItems[i];
    const itemHeight = scale(item.height);

    const availableHeight =
      chunks.length > 0 ? maxHeight - continuedHeight : maxHeight;

    const isNotifierHeader =
      item.type === DocketDisplayItemType.NOTIFIER_HEADER;
    const requiredHeight = isNotifierHeader
      ? getNotifierLeadHeight(filteredItems, i, scale)
      : itemHeight;
    const chunkHoldsOnlyNotifierLead = currentChunk.every(
      chunkItem =>
        chunkItem.type === DocketDisplayItemType.NOTIFIER_HEADER ||
        chunkItem.type === DocketDisplayItemType.NOTIFIER_PROFILE,
    );
    const keepsLeadWithRow = !isNotifierHeader && chunkHoldsOnlyNotifierLead;

    if (
      currentHeight + requiredHeight + continueHeight > availableHeight &&
      !keepsLeadWithRow
    ) {
      chunks.push(currentChunk);
      currentChunk = [];
      currentHeight = 0;
    }

    currentChunk.push(item);
    currentHeight += itemHeight;
  }

  if (currentChunk.length > 0) {
    chunks.push(currentChunk);
  }

  if (
    chunks.length > 1 &&
    filteredItems[0].type === DocketDisplayItemType.DOCKET_HEADER
  ) {
    const docketHeader = filteredItems[0];
    const docketId = (docketHeader as DocketDisplayOrderItem).id;
    docketHeader.position = docketHeader.position || {};
    const headerRowPosition = docketHeader.position.rowPosition || 1;
    const totalPages = chunks.length;

    for (let i = 0; i < chunks.length; i++) {
      const currentPage = i + 1;

      if (i < chunks.length - 1) {
        const pageIndicator = `${currentPage}/${totalPages}`;
        chunks[i].push({
          type: DocketDisplayItemType.CONTINUE,
          name: CONTINUE,
          height: DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUE],
          docketId: docketId,
          position: {
            parentRowPosition: headerRowPosition,
            pageIndicator: pageIndicator,
          },
        });
      }

      if (i > 0) {
        const pageIndicator = `${currentPage}/${totalPages}`;
        chunks[i].unshift({
          type: DocketDisplayItemType.CONTINUED,
          name: CONTINUE,
          height: DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUE],
          docketId: docketId,
          position: {
            parentRowPosition: headerRowPosition,
            pageIndicator: pageIndicator,
          },
        });
      }
    }
  }

  if (
    chunks.length > 1 &&
    filteredItems[0].type === DocketDisplayItemType.DOCKET_HEADER
  ) {
    filteredItems[0].position = filteredItems[0].position || {};
    const headerRowPosition = filteredItems[0].position.rowPosition || 1;

    for (let i = 1; i < chunks.length; i++) {
      const firstContentIndex = chunks[i].findIndex(
        item => item.type !== DocketDisplayItemType.CONTINUED,
      );

      if (firstContentIndex !== -1) {
        const firstContentItem = chunks[i][firstContentIndex];
        let chunkHeight = 0;
        for (let j = firstContentIndex; j < chunks[i].length; j++) {
          chunkHeight += scale(chunks[i][j].height);
        }
        const needsFullColumn = chunkHeight > rowHeight;

        chunks[i].forEach(item => {
          if (item.type !== DocketDisplayItemType.DOCKET_HEADER) {
            (item as DocketDisplayItem).position = {
              ...((item as DocketDisplayItem).position || {}),
              parentRowPosition: headerRowPosition,
            };

            if (needsFullColumn && item === firstContentItem) {
              (item as DocketDisplayItem).position = {
                ...((item as DocketDisplayItem).position || {}),
                needsFullColumn: true,
              };
            }
          }
        });
      }
    }
  }

  return chunks;
};
